import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Heart, Users, Phone, CalendarX, X } from 'lucide-react';

const ChildrenTab = ({
  children = [],
  loading = false,
  loadingChildren = false,
  onUpdate = () => {},
  // Optional props - will use local state if not provided
  editingChild: externalEditingChild,
  setEditingChild: externalSetEditingChild,
  childForm: externalChildForm,
  setChildForm: externalSetChildForm,
  savingChild: externalSavingChild,
  handleSaveChild: externalHandleSaveChild,
  // Absences - optional
  absences: externalAbsences,
  loadingAbsences: externalLoadingAbsences,
  showAbsenceForm: externalShowAbsenceForm,
  setShowAbsenceForm: externalSetShowAbsenceForm,
  absenceForm: externalAbsenceForm,
  setAbsenceForm: externalSetAbsenceForm,
  submittingAbsence: externalSubmittingAbsence,
  handleReportAbsence: externalHandleReportAbsence,
  handleCancelAbsence: externalHandleCancelAbsence
}) => {
  // Use local state if external state not provided
  const [localEditingChild, setLocalEditingChild] = useState(null);
  const [localChildForm, setLocalChildForm] = useState({});
  const [localSavingChild, setLocalSavingChild] = useState(false);
  
  const editingChild = externalEditingChild !== undefined ? externalEditingChild : localEditingChild;
  const setEditingChild = externalSetEditingChild || setLocalEditingChild;
  const childForm = externalChildForm !== undefined ? externalChildForm : localChildForm;
  const setChildForm = externalSetChildForm || setLocalChildForm;
  const savingChild = externalSavingChild !== undefined ? externalSavingChild : localSavingChild;
  
  // Absences - hide if not provided
  const absences = externalAbsences || [];
  const loadingAbsences = externalLoadingAbsences || false;
  const showAbsenceForm = externalShowAbsenceForm || false;
  const setShowAbsenceForm = externalSetShowAbsenceForm || (() => {});
  const absenceForm = externalAbsenceForm || { student_name: '', start_date: '', end_date: '', reason: '', notes: '' };
  const setAbsenceForm = externalSetAbsenceForm || (() => {});
  const submittingAbsence = externalSubmittingAbsence || false;
  const handleReportAbsence = externalHandleReportAbsence || (() => {});
  const handleCancelAbsence = externalHandleCancelAbsence || (() => {});
  
  // Also use loading from parent if loadingChildren not passed
  const isLoading = loadingChildren || loading;
  
  // Local save handler if not provided
  const handleSaveChild = externalHandleSaveChild || (async () => {
    // Basic save implementation using onUpdate
    setLocalSavingChild(true);
    try {
      // Here we'd make API call if needed
      onUpdate();
      setLocalEditingChild(null);
    } finally {
      setLocalSavingChild(false);
    }
  });
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
          <Heart className="w-5 h-5 text-pink-400" />
          My Children
        </h3>
        <p className="text-sm text-white/50">Update medical & emergency info</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full" />
        </div>
      ) : children.length === 0 ? (
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/50">No children registered yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {children.map((child, idx) => (
            <Card key={idx} className="bg-[#111] border-[#333]">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-white">{child.name}</h4>
                    <p className="text-sm text-white/50">Age: {child.age}</p>
                    {child.class_names?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {child.class_names.map((cls, i) => (
                          <Badge key={i} className="bg-pink-500/20 text-pink-400 border-pink-500/30 text-xs">{cls}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingChild(child);
                      setChildForm({
                        medical_conditions: child.medical_conditions || '',
                        allergies: child.allergies || '',
                        emergency_contact_name: child.emergency_contact_name || '',
                        emergency_contact_phone: child.emergency_contact_phone || '',
                        emergency_contact_relationship: child.emergency_contact_relationship || '',
                        notes: child.notes || ''
                      });
                    }}
                    className="border-pink-500/30 text-pink-400 hover:bg-pink-500/10"
                  >
                    Edit Info
                  </Button>
                </div>

                {editingChild?.name === child.name ? (
                  <div className="space-y-4 p-4 bg-[#1a1a1a] rounded-lg border border-pink-500/30">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white/70">Medical Conditions</Label>
                        <Textarea
                          value={childForm.medical_conditions}
                          onChange={(e) => setChildForm({...childForm, medical_conditions: e.target.value})}
                          placeholder="Any medical conditions, medications..."
                          className="bg-[#111] border-[#333] text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70">Allergies</Label>
                        <Textarea
                          value={childForm.allergies}
                          onChange={(e) => setChildForm({...childForm, allergies: e.target.value})}
                          placeholder="Food allergies, medication allergies..."
                          className="bg-[#111] border-[#333] text-white"
                        />
                      </div>
                    </div>

                    <div className="border-t border-[#333] pt-4">
                      <h5 className="text-white font-medium mb-3 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-pink-400" />
                        Emergency Contact
                      </h5>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-white/70">Contact Name</Label>
                          <Input
                            value={childForm.emergency_contact_name}
                            onChange={(e) => setChildForm({...childForm, emergency_contact_name: e.target.value})}
                            placeholder="Full name"
                            className="bg-[#111] border-[#333] text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-white/70">Phone Number</Label>
                          <Input
                            value={childForm.emergency_contact_phone}
                            onChange={(e) => setChildForm({...childForm, emergency_contact_phone: e.target.value})}
                            placeholder="07700 900000"
                            className="bg-[#111] border-[#333] text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-white/70">Relationship</Label>
                          <Input
                            value={childForm.emergency_contact_relationship}
                            onChange={(e) => setChildForm({...childForm, emergency_contact_relationship: e.target.value})}
                            placeholder="e.g., Grandparent, Aunt"
                            className="bg-[#111] border-[#333] text-white"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white/70">Additional Notes</Label>
                      <Textarea
                        value={childForm.notes}
                        onChange={(e) => setChildForm({...childForm, notes: e.target.value})}
                        placeholder="Any other important information..."
                        className="bg-[#111] border-[#333] text-white"
                      />
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setEditingChild(null)}
                        className="border-[#333] text-white"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveChild}
                        disabled={savingChild}
                        className="bg-pink-600 hover:bg-pink-700"
                      >
                        {savingChild ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div className="p-3 bg-[#1a1a1a] rounded-lg">
                      <p className="text-white/50 text-xs mb-1">Medical Conditions</p>
                      <p className="text-white">{child.medical_conditions || 'None specified'}</p>
                    </div>
                    <div className="p-3 bg-[#1a1a1a] rounded-lg">
                      <p className="text-white/50 text-xs mb-1">Allergies</p>
                      <p className="text-white">{child.allergies || 'None specified'}</p>
                    </div>
                    <div className="p-3 bg-[#1a1a1a] rounded-lg sm:col-span-2">
                      <p className="text-white/50 text-xs mb-1">Emergency Contact</p>
                      {child.emergency_contact_name ? (
                        <p className="text-white">
                          {child.emergency_contact_name} ({child.emergency_contact_relationship}) - {child.emergency_contact_phone}
                        </p>
                      ) : (
                        <p className="text-white/30">Not specified</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reported Absences - Only show if absences handler is provided */}
      {externalAbsences !== undefined && (
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
            <CalendarX className="w-5 h-5 text-orange-400" />
            Reported Absences
          </h3>
          <Button
            onClick={() => setShowAbsenceForm(true)}
            className="bg-orange-500 hover:bg-orange-600"
          >
            Report Absence
          </Button>
        </div>

        {/* Absence Form Modal */}
        {showAbsenceForm && (
          <Card className="bg-[#1a1a1a] border-orange-500/30 mb-4">
            <CardContent className="p-4 space-y-4">
              <h4 className="text-white font-medium">Report Future Absence</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Student Name *</Label>
                  <Select
                    value={absenceForm.student_name}
                    onValueChange={(v) => setAbsenceForm({...absenceForm, student_name: v})}
                  >
                    <SelectTrigger className="bg-[#111] border-[#333] text-white">
                      <SelectValue placeholder="Select student" />
                    </SelectTrigger>
                    <SelectContent>
                      {children.map((child, idx) => (
                        <SelectItem key={idx} value={child.name}>{child.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Reason *</Label>
                  <Select
                    value={absenceForm.reason}
                    onValueChange={(v) => setAbsenceForm({...absenceForm, reason: v})}
                  >
                    <SelectTrigger className="bg-[#111] border-[#333] text-white">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="illness">Illness</SelectItem>
                      <SelectItem value="holiday">Holiday</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Start Date *</Label>
                  <Input
                    type="date"
                    value={absenceForm.start_date}
                    onChange={(e) => setAbsenceForm({...absenceForm, start_date: e.target.value})}
                    className="bg-[#111] border-[#333] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">End Date *</Label>
                  <Input
                    type="date"
                    value={absenceForm.end_date}
                    onChange={(e) => setAbsenceForm({...absenceForm, end_date: e.target.value})}
                    className="bg-[#111] border-[#333] text-white"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Additional Notes</Label>
                <Textarea
                  value={absenceForm.notes}
                  onChange={(e) => setAbsenceForm({...absenceForm, notes: e.target.value})}
                  placeholder="Any additional information..."
                  className="bg-[#111] border-[#333] text-white"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAbsenceForm(false)}
                  className="border-[#333] text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReportAbsence}
                  disabled={submittingAbsence}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {submittingAbsence ? 'Submitting...' : 'Report Absence'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Absence List */}
        {loadingAbsences ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full" />
          </div>
        ) : absences.length === 0 ? (
          <Card className="bg-[#111] border-[#333]">
            <CardContent className="p-6 text-center">
              <CalendarX className="w-10 h-10 text-white/20 mx-auto mb-2" />
              <p className="text-white/50">No absences reported</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {absences.map(absence => (
              <Card key={absence.id} className="bg-[#111] border-[#333]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{absence.student_name}</p>
                      <p className="text-white/50 text-sm">
                        {new Date(absence.start_date).toLocaleDateString('en-GB')} - {new Date(absence.end_date).toLocaleDateString('en-GB')}
                      </p>
                      <Badge className={
                        absence.reason === 'illness' ? 'bg-red-500/20 text-red-400' :
                        absence.reason === 'holiday' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-gray-500/20 text-gray-400'
                      }>
                        {absence.reason.charAt(0).toUpperCase() + absence.reason.slice(1)}
                      </Badge>
                      {absence.notes && <p className="text-white/40 text-xs mt-1">{absence.notes}</p>}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelAbsence(absence.id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default ChildrenTab;
